import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, ShieldCheck, FileText, Tag, Crown, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Country code to name mapping for display - All countries
const countryNames = {
  "GB": "United Kingdom",
  "US": "United States",
  "CA": "Canada",
  "AU": "Australia",
  "NZ": "New Zealand",
  "IE": "Ireland",
  "DE": "Germany",
  "FR": "France",
  "ES": "Spain",
  "IT": "Italy",
  "NL": "Netherlands",
  "BE": "Belgium",
  "AT": "Austria",
  "PT": "Portugal",
  "PL": "Poland",
  "SE": "Sweden",
  "DK": "Denmark",
  "FI": "Finland",
  "NO": "Norway",
  "CH": "Switzerland",
  "GR": "Greece",
  "CZ": "Czech Republic",
  "HU": "Hungary",
  "RO": "Romania",
  "BG": "Bulgaria",
  "HR": "Croatia",
  "SK": "Slovakia",
  "SI": "Slovenia",
  "LT": "Lithuania",
  "LV": "Latvia",
  "EE": "Estonia",
  "CY": "Cyprus",
  "MT": "Malta",
  "LU": "Luxembourg",
  "IS": "Iceland",
  "JP": "Japan",
  "KR": "South Korea",
  "CN": "China",
  "HK": "Hong Kong",
  "SG": "Singapore",
  "MY": "Malaysia",
  "TH": "Thailand",
  "IN": "India",
  "PK": "Pakistan",
  "BD": "Bangladesh",
  "AE": "United Arab Emirates",
  "SA": "Saudi Arabia",
  "QA": "Qatar",
  "KW": "Kuwait",
  "BH": "Bahrain",
  "OM": "Oman",
  "IL": "Israel",
  "TR": "Turkey",
  "ZA": "South Africa",
  "EG": "Egypt",
  "NG": "Nigeria",
  "KE": "Kenya",
  "GH": "Ghana",
  "MA": "Morocco",
  "MX": "Mexico",
  "BR": "Brazil",
  "AR": "Argentina",
  "CL": "Chile",
  "CO": "Colombia",
  "PE": "Peru",
  "VE": "Venezuela",
  "RU": "Russia",
  "UA": "Ukraine",
  "PH": "Philippines",
  "ID": "Indonesia",
  "VN": "Vietnam",
  "TW": "Taiwan",
  "OTHER": "Other"
};

export const Checkout = () => {
  const navigate = useNavigate();
  const { cart, sessionId } = useCart();
  const { user, isAuthenticated, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [shippingEstimate, setShippingEstimate] = useState(null);
  const [orderInstructions, setOrderInstructions] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [loyaltyDiscount, setLoyaltyDiscount] = useState(null);
  const [loyaltyInfo, setLoyaltyInfo] = useState(null);
  
  // Load shipping estimate, order instructions, discount, and loyalty info
  useEffect(() => {
    const savedShipping = localStorage.getItem('shipping_estimate');
    if (savedShipping) {
      try {
        setShippingEstimate(JSON.parse(savedShipping));
      } catch (e) {
        console.error('Error parsing shipping estimate:', e);
      }
    }
    const savedInstructions = localStorage.getItem('order_instructions');
    if (savedInstructions) {
      setOrderInstructions(savedInstructions);
    }
    const savedDiscount = localStorage.getItem('applied_discount');
    if (savedDiscount) {
      try {
        const discount = JSON.parse(savedDiscount);
        // Recalculate discount based on current cart total
        discount.discount_amount = (cart.total * discount.discount_percent) / 100;
        setAppliedDiscount(discount);
      } catch (e) {
        console.error('Error parsing discount:', e);
      }
    }
    
    // Fetch loyalty status for logged-in users
    if (isAuthenticated) {
      fetchLoyaltyStatus();
    }
  }, [cart.total, isAuthenticated]);
  
  const fetchLoyaltyStatus = async () => {
    try {
      const response = await axios.get(`${API}/loyalty/member`, {
        headers: getAuthHeader()
      });
      const data = response.data;
      setLoyaltyInfo(data);
      
      // Apply loyalty discount if user has one
      if (data.current_tier?.discount_percent > 0) {
        const discountPercent = data.current_tier.discount_percent;
        const discountAmount = (cart.total * discountPercent) / 100;
        setLoyaltyDiscount({
          tier_name: data.current_tier.name,
          discount_percent: discountPercent,
          discount_amount: discountAmount
        });
      }
    } catch (error) {
      console.error('Error fetching loyalty status:', error);
    }
  };
  
  const [formData, setFormData] = useState({
    email: user?.email || "",
    firstName: "",
    lastName: "",
    address: "",
    city: "",
    postcode: "",
    country: shippingEstimate?.country ? (countryNames[shippingEstimate.country] || "United Kingdom") : "United Kingdom",
    phone: ""
  });

  // Calculate VAT based on shipping country
  const isUKOrder = shippingEstimate?.isUKOrder !== false && (shippingEstimate?.country === "GB" || !shippingEstimate?.country);
  const vatRate = isUKOrder ? 0.2 : 0;
  const couponDiscountAmount = appliedDiscount?.discount_amount || 0;
  const loyaltyDiscountAmount = loyaltyDiscount?.discount_amount || 0;
  const totalDiscountAmount = couponDiscountAmount + loyaltyDiscountAmount;
  const subtotalAfterDiscount = cart.total - totalDiscountAmount;
  const shippingCost = shippingEstimate?.cost || 0;
  const subtotalWithShipping = subtotalAfterDiscount + shippingCost;
  const vatAmount = subtotalWithShipping * vatRate;
  const totalWithVat = subtotalWithShipping + vatAmount;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (cart.items.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    // Validate required fields
    const required = ["firstName", "lastName", "address", "city", "postcode"];
    if (!isAuthenticated) {
      required.push("email");
    }
    
    for (const field of required) {
      if (!formData[field]) {
        toast.error(`Please fill in ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
        return;
      }
    }

    setLoading(true);
    
    try {
      const shippingAddress = {
        name: `${formData.firstName} ${formData.lastName}`,
        address: formData.address,
        city: formData.city,
        postcode: formData.postcode,
        country: formData.country,
        phone: formData.phone
      };

      const response = await axios.post(
        `${API}/payments/stripe/checkout`,
        {
          cart_session_id: sessionId,
          shipping_address: shippingAddress,
          guest_email: !isAuthenticated ? formData.email : null,
          shipping_cost: shippingEstimate?.cost || 0,
          shipping_method: shippingEstimate?.method || null,
          order_instructions: orderInstructions || null,
          is_uk_order: isUKOrder,
          vat_rate: vatRate,
          discount_code: appliedDiscount?.code || null,
          discount_amount: couponDiscountAmount || 0,
          loyalty_discount_percent: loyaltyDiscount?.discount_percent || 0,
          loyalty_discount_amount: loyaltyDiscountAmount || 0,
          loyalty_tier: loyaltyDiscount?.tier_name || null
        },
        { headers: getAuthHeader() }
      );

      // If there's a discount code, store it for marking as used after payment
      if (appliedDiscount?.code) {
        localStorage.setItem('pending_discount_code', appliedDiscount.code);
      }

      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(error.response?.data?.detail || "Checkout failed. Please try again.");
      setLoading(false);
    }
  };

  if (cart.items.length === 0) {
    return (
      <div className="pt-24 pb-16">
        <div className="max-w-2xl mx-auto px-4 md:px-8 text-center py-16">
          <h1 className="text-2xl font-heading font-bold mb-4">Your cart is empty</h1>
          <Link to="/products">
            <Button className="rounded-full uppercase tracking-widest">
              Continue Shopping
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-16" data-testid="checkout-page">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="label-style mb-2">Secure</p>
            <h1 className="section-title">Checkout</h1>
          </div>
          <div className="flex items-center gap-1 text-green-600">
            <ShieldCheck className="h-5 w-5" />
            <span className="text-xs font-medium">SSL Secure</span>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Checkout Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Contact */}
            {!isAuthenticated && (
              <div className="space-y-4">
                <h2 className="font-heading font-bold text-lg uppercase tracking-wider">Contact</h2>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="rounded-full mt-1"
                    placeholder="your@email.com"
                    required
                    data-testid="email-input"
                  />
                </div>
                <p className="text-sm text-muted-foreground">
                  Already have an account?{" "}
                  <Link to="/login" className="text-accent hover:underline">
                    Log in
                  </Link>
                </p>
              </div>
            )}

            {/* Shipping */}
            <div className="space-y-4">
              <h2 className="font-heading font-bold text-lg uppercase tracking-wider">Shipping Address</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className="rounded-full mt-1"
                    required
                    data-testid="firstname-input"
                  />
                </div>
                <div>
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className="rounded-full mt-1"
                    required
                    data-testid="lastname-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="rounded-full mt-1"
                  required
                  data-testid="address-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="rounded-full mt-1"
                    required
                    data-testid="city-input"
                  />
                </div>
                <div>
                  <Label htmlFor="postcode">Postcode</Label>
                  <Input
                    id="postcode"
                    name="postcode"
                    value={formData.postcode}
                    onChange={handleChange}
                    className="rounded-full mt-1"
                    required
                    data-testid="postcode-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="country">Country</Label>
                <select
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className="w-full h-10 px-4 mt-1 rounded-full border border-input bg-white text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  required
                  data-testid="country-select"
                >
                  {Object.entries(countryNames).map(([code, name]) => (
                    <option key={code} value={name}>{name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="phone">Phone (optional)</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="rounded-full mt-1"
                  data-testid="phone-input"
                />
              </div>
            </div>

            {/* Order Instructions */}
            <div className="space-y-4">
              <h2 className="font-heading font-bold text-lg uppercase tracking-wider flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Add further Instruction to order
              </h2>
              <textarea
                value={orderInstructions}
                onChange={(e) => setOrderInstructions(e.target.value)}
                placeholder="Enter any special instructions for your order (e.g., custom text, specific requirements)..."
                className="w-full px-3 py-2 border border-input rounded-full text-sm bg-white min-h-[80px] resize-none"
                data-testid="checkout-order-instructions"
              />
            </div>

            {/* Payment Method */}
            <div className="space-y-4">
              <h2 className="font-heading font-bold text-lg uppercase tracking-wider">Payment Method</h2>
              <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                <div className="flex items-center space-x-3 border border-input p-4">
                  <RadioGroupItem value="stripe" id="stripe" />
                  <Label htmlFor="stripe" className="flex-1 cursor-pointer">
                    <span className="font-medium">Credit / Debit Card</span>
                    <span className="text-sm text-muted-foreground block">Powered by Stripe</span>
                  </Label>
                </div>
                <div className="flex items-center space-x-3 border border-input p-4 opacity-50">
                  <RadioGroupItem value="paypal" id="paypal" disabled />
                  <Label htmlFor="paypal" className="flex-1 cursor-pointer">
                    <span className="font-medium">PayPal</span>
                    <span className="text-sm text-muted-foreground block">Coming soon</span>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <Button
              type="submit"
              size="lg"
              className="w-full rounded-full h-14 uppercase tracking-widest font-bold"
              disabled={loading}
              data-testid="place-order-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Processing...
                </>
              ) : (
                `Pay £${totalWithVat.toFixed(2)}`
              )}
            </Button>
          </form>

          {/* Order Summary */}
          <div className="lg:pl-12 lg:border-l border-border">
            <h2 className="font-heading font-bold text-lg uppercase tracking-wider mb-6">Order Summary</h2>
            <div className="space-y-4">
              {cart.items.map((item) => (
                <div key={item.product_id} className="flex gap-4">
                  <div className="relative">
                    <img
                      src={item.image_url || "https://images.unsplash.com/photo-1766602110905-e979239fd291?w=200"}
                      alt={item.name}
                      className="w-16 h-16 object-cover bg-secondary"
                    />
                    <span className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-accent text-accent-foreground text-xs flex items-center justify-center">
                      {item.quantity}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{item.name}</p>
                    <p className="text-sm text-muted-foreground">Qty: {item.quantity}</p>
                    {item.bulk_discount_applied && (
                      <p className="text-xs text-green-600 flex items-center gap-1">
                        <Tag className="h-3 w-3" />
                        Bulk discount @ £26.75/unit
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="price-font font-medium">£{item.item_total.toFixed(2)}</p>
                    {item.bulk_discount_applied && item.original_price && (
                      <p className="text-xs text-green-600">
                        Save £{((item.original_price - item.price) * item.quantity).toFixed(2)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-border mt-6 pt-6 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span>£{cart.total.toFixed(2)}</span>
              </div>
              {loyaltyDiscount && loyaltyDiscount.discount_amount > 0 && (
                <div className="flex justify-between text-sm text-purple-600" data-testid="loyalty-discount-row">
                  <span className="flex items-center gap-1">
                    <Crown className="h-3 w-3" />
                    {loyaltyDiscount.tier_name} Member ({loyaltyDiscount.discount_percent}% off)
                  </span>
                  <span>-£{loyaltyDiscount.discount_amount.toFixed(2)}</span>
                </div>
              )}
              {appliedDiscount && (
                <div className="flex justify-between text-sm text-green-600">
                  <span className="flex items-center gap-1">
                    <Tag className="h-3 w-3" />
                    Discount ({appliedDiscount.discount_percent}%)
                  </span>
                  <span>-£{couponDiscountAmount.toFixed(2)}</span>
                </div>
              )}
              {shippingEstimate && shippingEstimate.cost > 0 && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      Shipping
                      <span className="text-xs ml-1">({shippingEstimate.method})</span>
                    </span>
                    <span>£{shippingEstimate.cost.toFixed(2)}</span>
                  </div>
                  {shippingEstimate.details && (
                    <p className="text-xs text-muted-foreground bg-secondary/50 p-2 rounded">
                      {shippingEstimate.details}
                    </p>
                  )}
                </>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {isUKOrder ? "VAT (20%)" : "VAT (Export - 0%)"}
                </span>
                <span>£{vatAmount.toFixed(2)}</span>
              </div>
              {!isUKOrder && (
                <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                  VAT exempt for international orders. You may be liable for local import duties/taxes upon delivery.
                </p>
              )}
              <div className="flex justify-between text-lg font-bold pt-3 border-t border-border">
                <span>Total</span>
                <span className="price-font">£{totalWithVat.toFixed(2)}</span>
              </div>
            </div>

            {/* SSL Secure Badge */}
            <div className="mt-6 pt-4 border-t border-border flex items-center justify-center gap-2 text-green-600">
              <ShieldCheck className="h-5 w-5" />
              <span className="text-sm font-medium">SSL Secure</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
